import { useContext, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@/contexts/AuthContext';
import { useWorkflowStore } from '@/state/workflow.state';
import { fetchWorkflowContent } from '@/components/Recipe/FlowComponents/Share/workflowVersionContent';
import type { PublishWorkflowSource } from '@/components/Recipe/FlowComponents/Share/publishWorkflowSource';
import type { ToolView } from '@/api/toolView.api';
import type { Node } from '@/types/node';

const TOOL_PAGE_PUBLISH_WORKFLOW_KEY = 'tool-page-publish-workflow';
const NO_NODES: Node[] = [];

/** Stands in while the workflow loads: a guest with no nodes, so nothing can be published from it. */
export const loadingPublishWorkflowSource = (workflowId: string, name: string): PublishWorkflowSource => ({
  id: workflowId,
  name,
  role: 'guest',
  nodes: NO_NODES,
  getLatestNodes: () => NO_NODES,
  adoptDraftVersion: () => {},
});

/**
 * The workflow the `/tool` Share modal publishes. The page has no editor, so it fetches the workflow's
 * draft for the readiness checks; the server freezes that same draft when the publish starts.
 * Only a tool creator who still owns its workflow can publish, the rule the editor uses for its
 * `editor` role. `source` is null until a fresh read and the node types have loaded, and stays null
 * (no longer loading) when either fails or the caller doesn't own the workflow.
 */
export function useToolPagePublishWorkflowSource(
  tool: ToolView | undefined,
  enabled: boolean,
): { source: PublishWorkflowSource | null; isLoading: boolean } {
  const { currentUser } = useContext(AuthContext);
  const workflowId = tool?.workflowId;
  const isEnabled = enabled && tool?.isOwner === true && !!workflowId;
  const queryKey = useMemo(() => [TOOL_PAGE_PUBLISH_WORKFLOW_KEY, workflowId] as const, [workflowId]);
  const {
    data: workflow,
    isError,
    isFetchedAfterMount,
  } = useQuery({
    queryKey,
    queryFn: () => fetchWorkflowContent(workflowId!),
    enabled: isEnabled,
    staleTime: 0,
    // The author may edit the draft in another tab while Share is open; reread it on return.
    refetchOnWindowFocus: 'always',
    retry: false,
  });

  // Without node types every model reads as unverified, so the readiness check waits for them.
  const nodeTypesLoadingState = useWorkflowStore((state) => state.loadingStates.get('node-types'));
  const loadNodeTypes = useWorkflowStore((state) => state.loadNodeTypes);
  // An earlier failure is retried once per open, not on every render that sees it.
  const hasRetriedNodeTypesRef = useRef(false);
  useEffect(() => {
    if (!isEnabled) return;
    if (nodeTypesLoadingState === 'initial') void loadNodeTypes();
    if (nodeTypesLoadingState === 'error' && !hasRetriedNodeTypesRef.current) {
      hasRetriedNodeTypesRef.current = true;
      void loadNodeTypes();
    }
  }, [isEnabled, loadNodeTypes, nodeTypesLoadingState]);

  // The modal mounts on each open, so this waits for a read of the current draft rather than
  // checking a cached one the author has edited since.
  const freshWorkflow = isFetchedAfterMount ? workflow : undefined;
  const isWorkflowEditor =
    freshWorkflow != null &&
    freshWorkflow.owner === currentUser?.uid &&
    (!freshWorkflow.latestVersion || freshWorkflow.version === freshWorkflow.latestVersion.version);
  const isNodeTypesSettled = nodeTypesLoadingState === 'loaded' || nodeTypesLoadingState === 'error';

  const source = useMemo<PublishWorkflowSource | null>(() => {
    if (!isEnabled || !freshWorkflow || !isWorkflowEditor || nodeTypesLoadingState !== 'loaded') return null;
    const nodes = freshWorkflow.nodes ?? [];
    return {
      id: freshWorkflow.id,
      name: freshWorkflow.name,
      role: 'editor',
      nodes,
      getLatestNodes: () => nodes,
      // No editor holds this draft. Refetching now would swap the source mid-publish; the next
      // open reads the draft the server forked anyway.
      adoptDraftVersion: () => {},
    };
  }, [freshWorkflow, isEnabled, isWorkflowEditor, nodeTypesLoadingState]);
  // A cached error from an earlier open doesn't count until this open's read settles.
  const hasReadFailed = isError && isFetchedAfterMount;
  const isLoading = isEnabled && !hasReadFailed && (!freshWorkflow || (isWorkflowEditor && !isNodeTypesSettled));

  return { source, isLoading };
}
